import { RepeatIcon } from '@itsrever/design-system'
import { CartItem } from './CartItem'
import { useAppSelector } from '@/redux/hooks'
import { NewItem } from '@/domain/new-item'
import { CartEmptyState } from './CartEmptyState'
import { useTranslation } from 'react-i18next'
import { useCart } from '@/application/use-cart/use-cart'

interface CartViewProps {
    returningItemId: string
    onClose?: () => void
    handleContinue?: () => void
}

export const CartView: React.FC<CartViewProps> = ({
    returningItemId,
    onClose,
    handleContinue
}) => {
    const { cartItems } = useCart()

    const orderItems = useAppSelector((store) => store.order.orderProducts)

    const settings = useAppSelector(
        (store) => store.settingsApi.settings.response
    )
    const { t } = useTranslation()

    const groupedCartItems = cartItems.reduce((acc, item) => {
        if (!acc[item.returnedItemId]) {
            acc[item.returnedItemId] = []
        }
        acc[item.returnedItemId].push(item)
        return acc
    }, {} as Record<string, NewItem[]>)

    const sortedGroupedCartItems = Object.entries(groupedCartItems).sort(
        ([a], [b]) => {
            const indexA = orderItems.findIndex(
                (orderItem) => orderItem.lineItemId === a
            )
            const indexB = orderItems.findIndex(
                (orderItem) => orderItem.lineItemId === b
            )
            return indexA - indexB
        }
    )

    const isHoff = settings.ecommerce_group === 'hoff'
    const isMisako = settings.ecommerce_group === 'misako'

    if (isHoff) {
        return (
            <div className="mb-20 flex flex-col gap-4 overflow-scroll">
                {sortedGroupedCartItems.length ? (
                    sortedGroupedCartItems.map(([returnedItemId, items]) => {
                        const returnedItemName =
                            orderItems.find(
                                (orderItem) =>
                                    orderItem.lineItemId === returnedItemId
                            )?.product.name || 'Unknown Item'

                        return (
                            <>
                                <div key={returnedItemId} className="mb-6">
                                    <p className="mb-3 flex items-center gap-2 font-haas text-base font-normal text-black">
                                        {returnedItemName}
                                        <svg
                                            width="256"
                                            height="256"
                                            viewBox="0 0 256 256"
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="size-4"
                                        >
                                            <path
                                                fill="currentColor"
                                                d="m229.66 165.66l-48 48a8 8 0 0 1-11.32 0l-48-48A8 8 0 0 1 128 152h40V72H32a8 8 0 0 1 0-16h144a8 8 0 0 1 8 8v88h40a8 8 0 0 1 5.66 13.66"
                                            />
                                        </svg>
                                    </p>
                                    <div className="flex flex-col border-t-1 border-solid border-black">
                                        {items.map((item, index) => (
                                            <CartItem
                                                key={index}
                                                item={item}
                                                index={index}
                                                returningItemId={
                                                    returningItemId
                                                }
                                            />
                                        ))}
                                    </div>
                                </div>
                            </>
                        )
                    })
                ) : (
                    <div className="flex w-full justify-center">
                        <div className="mt-10">
                            <CartEmptyState onClose={onClose} />
                        </div>
                    </div>
                )}

                {sortedGroupedCartItems.length > 0 ? (
                    <>
                        {sortedGroupedCartItems.length ? (
                            <button
                                onClick={handleContinue}
                                id="fc-continue-button"
                                className="ml-auto w-fit min-w-44 cursor-pointer rounded-xl bg-black p-3 font-haas text-sm text-white disabled:cursor-not-allowed disabled:bg-neutral-500"
                            >
                                <span className="mx-6 flex items-center justify-center gap-3 font-haas text-base">
                                    {t('full_catalog.confirm')}
                                </span>
                            </button>
                        ) : null}
                    </>
                ) : null}
            </div>
        )
    }

    if (isMisako) {
        return (
            <div className="flex h-full flex-col gap-4">
                {sortedGroupedCartItems.length ? (
                    sortedGroupedCartItems.map(([returnedItemId, items]) => {
                        const returnedItemName =
                            orderItems.find(
                                (orderItem) =>
                                    orderItem.lineItemId === returnedItemId
                            )?.product.name || 'Unknown Item'

                        return (
                            <div key={returnedItemId} className="mb-6">
                                <p className="mb-3 flex items-center gap-2 text-base font-[300] tracking-wider text-neutral-500 [font-family:Inter]">
                                    <RepeatIcon />
                                    {returnedItemName}
                                </p>
                                <div className="flex flex-col gap-3">
                                    {items.map((item, index) => (
                                        <CartItem
                                            key={index}
                                            item={item}
                                            index={index}
                                            returningItemId={returningItemId}
                                        />
                                    ))}
                                </div>
                            </div>
                        )
                    })
                ) : (
                    <div className="flex w-full justify-center">
                        <div className="mt-10">
                            <CartEmptyState onClose={onClose} />
                        </div>
                    </div>
                )}
                <div className="flex w-full md:hidden">
                    <button
                        id="item-selection-continue-button"
                        onClick={handleContinue}
                        className="mx-auto mt-auto w-56 bg-black p-3 text-center font-haas text-sm text-white disabled:cursor-not-allowed disabled:bg-neutral-500"
                    >
                        <span className="text-[13px] font-[300] uppercase tracking-wide [font-family:Inter]">
                            {t('full_catalog.confirm')}
                        </span>
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-4">
            {sortedGroupedCartItems.length ? (
                sortedGroupedCartItems.map(([returnedItemId, items]) => {
                    const returnedItemName =
                        orderItems.find(
                            (orderItem) =>
                                orderItem.lineItemId === returnedItemId
                        )?.product.name || 'Unknown Item'

                    return (
                        <div key={returnedItemId} className="mb-6">
                            <p className="mb-3 flex items-center gap-2 text-base text-neutral-500">
                                <RepeatIcon />
                                {returnedItemName}
                            </p>
                            <div className="flex flex-col gap-3">
                                {items.map((item, index) => (
                                    <CartItem
                                        key={index}
                                        item={item}
                                        index={index}
                                        returningItemId={returningItemId}
                                    />
                                ))}
                            </div>
                        </div>
                    )
                })
            ) : (
                <div className="flex w-full justify-center">
                    <div className="mt-10">
                        <CartEmptyState onClose={onClose} />
                    </div>
                </div>
            )}
        </div>
    )
}
